import './default-theme.processed.css';
import './index.css';
import './learnitive-design.css';
import './saas.css';
import './ws-learnitive.css';

import { consumePwaLaunchParams } from '@learnitive/app/src/common/pwa-install';
import { ThemeManager } from '@learnitive/color-scheme-manager';
import { APP_BUILD_ID, APP_BUILD_TIME } from '@learnitive/config';
import { THEME_MANAGER_CONFIG } from '@learnitive/constants';
import {
  createEditorSaveCoordinator,
  initializeServices,
} from '@learnitive/initialize-services';
import { Logger } from '@learnitive/logger';
import { t } from '@learnitive/translations';
import { createStore } from 'jotai';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { setupAppBuildGuard } from './app-build-guard';
import { setupRootEmitter } from './setup-root-emitter';
import { LearnitiveSiteTelemetry } from './site-telemetry';
import {
  installLearnitiveLocationChangeBridge,
  resolveLearnitiveBootSurface,
  LEARNITIVE_LOCATION_CHANGE_EVENT,
  type LearnitiveBootSurface,
} from './ws-boot-surface';

const isDebug =
  window.location.hostname === 'localhost' ||
  window.location.search.includes('debug=true');

const startupLogger = new Logger('', isDebug ? 'debug' : 'info');
const editorSaveCoordinator = createEditorSaveCoordinator();

let editorModulePromise: ReturnType<typeof importEditorModule> | undefined;
let saasModulePromise: ReturnType<typeof importSaasModule> | undefined;
function importEditorModule() { return import('@learnitive/app'); }
function importSaasModule() { return import('@learnitive/app/src/saas/saas-app'); }
function loadEditorModule() { return editorModulePromise ??= importEditorModule(); }
function loadSaasModule() { return saasModulePromise ??= importSaasModule(); }

const LazyLearnitiveEditorApp = React.lazy(async () => {
  const module = await loadEditorModule();
  return { default: module.App };
});

const LazyLearnitiveSaasApp = React.lazy(async () => {
  const module = await loadSaasModule();
  return { default: module.SaasApp };
});

async function preloadLearnitiveSurface(surface: Exclude<LearnitiveBootSurface, 'home' | 'projects'>) {
  if (surface === 'editor') await loadEditorModule();
  else await loadSaasModule();
}

function installBootstrapProgress() {
  const existing = document.querySelector<HTMLElement>('[data-learnitive-bootstrap-screen]');
  if (existing) {
    document.body.setAttribute('aria-busy', 'true');
    return existing;
  }
  const element = document.createElement('div');
  element.id = 'learnitive-bootstrap-screen';
  element.dataset.learnitiveBootstrapScreen = 'true';
  element.setAttribute('aria-hidden', 'true');
  element.innerHTML = '<i></i>';
  document.body.append(element);
  document.body.setAttribute('aria-busy', 'true');
  return element;
}

function finishBootstrapProgress(element: HTMLElement) {
  if (document.documentElement.dataset.learnitiveAppReady === 'true') return;
  document.documentElement.dataset.learnitiveAppReady = 'true';
  document.body.removeAttribute('aria-busy');
  const remove = () => { if (element.isConnected) element.remove(); };
  element.addEventListener('transitionend', remove, { once: true });
  window.setTimeout(remove, 350);
}

const LEARNITIVE_REQUIRED_FONT_FACES = [
  '400 16px "Learnitive Inter"',
  '400 16px "Learnitive JetBrains Mono"',
  '300 16px "Learnitive Moranga"',
  '400 16px "Learnitive Moranga"',
  'italic 400 16px "Learnitive Moranga"',
  '700 16px "Learnitive Moranga"',
  '400 16px "Learnitive STIX Two Text"',
  'italic 400 16px "Learnitive STIX Two Text"',
] as const;

async function waitForLearnitiveFonts(logger: Logger) {
  if (!('fonts' in document)) return;
  const timeout = new Promise<void>((resolve) => window.setTimeout(resolve, 4_000));
  try {
    await Promise.race([Promise.all(LEARNITIVE_REQUIRED_FONT_FACES.map((face) => document.fonts.load(face))).then(() => undefined), timeout]);
    const missing = LEARNITIVE_REQUIRED_FONT_FACES.filter((face) => !document.fonts.check(face));
    if (missing.length) logger.warn('Learnitive font boot fallback: some preloaded fonts were not ready before the failsafe timeout', missing);
  } catch (error) {
    logger.warn('Learnitive font boot fallback: unable to preflight fonts', error);
  }
}

async function main(logger: Logger) {
  installLearnitiveLocationChangeBridge(window);
  registerLearnitiveServiceWorker(logger);
  // Consume PWA launch params (?launch=, ?shortcut=) before any service is
  // constructed: the router captures `window.location.search` at creation
  // and re-emits it on every navigation, so a param still present here
  // would be re-added to the URL and replayed on reload. Idempotent, so the
  // in-app fallback consumer stays harmless.
  consumePwaLaunchParams(window);

  // Capture the browser entry intent before initializeServices(). The hash
  // router is constructed during service initialization and startup recovery
  // may normalize browser history before React is mounted. A valid /ws hash
  // deep link must therefore remain authoritative even if location.hash changes
  // while services are starting.
  const bootLocation = {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };

  const bootSurface = resolveLearnitiveBootSurface(bootLocation.pathname, bootLocation.hash);
  if (bootSurface === 'home') {
    window.location.replace('/');
    return;
  }
  if (bootSurface === 'projects') {
    window.location.replace('/projects');
    return;
  }

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  // Keep the SEO prerender in the document, but cover it with the inline bootstrap screen
  // until the route chunk, application CSS, core services and required brand fonts are ready.
  const bootstrapProgress = installBootstrapProgress();
  const fontPreflight = waitForLearnitiveFonts(logger).finally(() => { document.documentElement.dataset.learnitiveFontsReady = 'true'; });
  const surfacePreload = preloadLearnitiveSurface(bootSurface === 'editor' ? 'editor' : 'saas');

  const abortController = new AbortController();
  const tabId = `tab_${Math.random().toString(36).substr(2, 9)}`;

  const rootEmitter = setupRootEmitter(
    'learnitive_io_channel',
    tabId,
    logger,
    abortController.signal,
  );
  const appBuildGuard = setupAppBuildGuard({
    rootEmitter,
    appBuild: { id: APP_BUILD_ID, builtAt: APP_BUILD_TIME },
    tabId,
    logger: logger.child('app-build-guard'),
    abortSignal: abortController.signal,
  });

  const store = createStore();
  const themeManager = new ThemeManager(THEME_MANAGER_CONFIG);
  let services: Awaited<ReturnType<typeof initializeServices>>;
  try {
    services = await initializeServices(
      logger,
      rootEmitter,
      store,
      themeManager,
      abortController.signal,
      editorSaveCoordinator,
    );
  } catch (error) {
    bootstrapProgress.remove();
    abortController.abort();
    throw error;
  }
  try {
    await Promise.all([surfacePreload, fontPreflight]);
  } catch (error) {
    bootstrapProgress.remove();
    document.body.removeAttribute('aria-busy');
    abortController.abort();
    throw error;
  }

  const currentSurface = resolveLearnitiveBootSurface(window.location.pathname, window.location.hash);

  // If service initialization normalized the browser URL, restore the exact
  // editor deep link captured at boot before React mounts. Hash fragments never
  // reach Apache, so this is entirely browser-local and preserves the selected
  // workspace/file.
  if (bootSurface === 'editor' && currentSurface !== 'editor') {
    window.history.replaceState(
      window.history.state,
      '',
      `${bootLocation.pathname}${bootLocation.search}${bootLocation.hash}`,
    );
  }

  const initialSurface = resolveLearnitiveBootSurface(window.location.pathname, window.location.hash);
  if (initialSurface === 'home') {
    window.location.replace('/');
    return;
  }
  if (initialSurface === 'projects') {
    window.location.replace('/projects');
    return;
  }

  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <LearnitiveRootApplication
        initialSurface={bootSurface === 'editor' ? 'editor' : initialSurface}
        logger={logger}
        services={services}
        store={store}
        rootEmitter={rootEmitter}
        onApplicationReady={() => finishBootstrapProgress(bootstrapProgress)}
      />
    </StrictMode>,
  );

  // Services (including the stale-tab listener) must be mounted before a
  // running tab can reply to this announcement.
  appBuildGuard.start();

  if (isDebug) {
    (window as any).services = services;
  }

  abortController.signal.addEventListener('abort', () => {
    root.unmount();
  });

  rootEmitter.on(
    'event::app:reload-ui',
    () => {
      logger.info('-------------Reloading UI-------------');
      abortController.abort();
      queueMicrotask(() => {
        void main(logger).catch((error) => {
          handleStartupFailure(error, logger);
        });
      });
    },
    abortController.signal,
  );
}


type LearnitiveRootApplicationProps = {
  initialSurface: Exclude<LearnitiveBootSurface, 'home' | 'projects'>;
  logger: Logger;
  services: Awaited<ReturnType<typeof initializeServices>>;
  store: ReturnType<typeof createStore>;
  rootEmitter: ReturnType<typeof setupRootEmitter>;
  onApplicationReady: () => void;
};

/**
 * Live top-level route boundary. This is deliberately outside SaasApp/App so a
 * history/hash transition to /ws can never be rendered by the SaaS 404 route.
 */
function LearnitiveRootApplication({
  initialSurface,
  logger,
  services,
  store,
  rootEmitter,
  onApplicationReady,
}: LearnitiveRootApplicationProps) {
  const [surface, setSurface] = React.useState<Exclude<LearnitiveBootSurface, 'home' | 'projects'>>(initialSurface);

  React.useEffect(() => {
    const synchronize = () => {
      const next = resolveLearnitiveBootSurface(window.location.pathname, window.location.hash);
      if (next === 'home') {
        window.location.replace('/');
        return;
      }
      if (next === 'projects') {
        window.location.replace('/projects');
        return;
      }
      setSurface(next);
    };
    window.addEventListener('hashchange', synchronize);
    window.addEventListener('popstate', synchronize);
    window.addEventListener(LEARNITIVE_LOCATION_CHANGE_EVENT, synchronize);
    return () => {
      window.removeEventListener('hashchange', synchronize);
      window.removeEventListener('popstate', synchronize);
      window.removeEventListener(LEARNITIVE_LOCATION_CHANGE_EVENT, synchronize);
    };
  }, []);

  document.documentElement.dataset.learnitiveSurface = surface;
  const RootApplication = surface === 'editor' ? LazyLearnitiveEditorApp : LazyLearnitiveSaasApp;
  return (
    <>
      <React.Suspense fallback={<SurfaceLoadingSkeleton />}>
        <RootApplication
          logger={logger}
          services={services}
          store={store}
          rootEmitter={rootEmitter}
        />
        <ApplicationPaintReady onReady={onApplicationReady} />
      </React.Suspense>
      <LearnitiveSiteTelemetry />
    </>
  );
}


function ApplicationPaintReady({ onReady }: { onReady: () => void }) {
  React.useLayoutEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(onReady);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [onReady]);
  return null;
}


function SurfaceLoadingSkeleton() {
  return <main className="interactive-route-skeleton" aria-hidden="true">
    <div className="interactive-route-skeleton-heading"><i /><i /></div>
    <div className="interactive-route-skeleton-grid"><span /><span /><span /></div>
  </main>;
}


function handleStartupFailure(error: unknown, logger: Logger) {
  logger.error('Unable to start Learnitive', error);
  renderStartupError(error);
}

export function renderStartupError(error: unknown) {
  document.documentElement.dataset.learnitiveAppReady = 'true';
  document.body.removeAttribute('aria-busy');
  document.querySelector<HTMLElement>('[data-learnitive-bootstrap-screen]')?.remove();
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Unable to show startup failure because #root is missing');
    return;
  }

  createRoot(rootElement).render(
    <StrictMode>
      <StartupErrorView error={error} />
    </StrictMode>,
  );
}

function StartupErrorView({ error }: { error: unknown }) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : t.app.pageStartupError.description;

  return (
    <main
      role="alert"
      aria-labelledby="startup-error-title"
      className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground"
    >
      <section className="w-full max-w-xl rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
        <h1 id="startup-error-title" className="font-semibold text-2xl">
          {t.app.pageStartupError.title}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {t.app.pageStartupError.description}
        </p>
        <details className="mt-4 rounded-md border bg-muted/40 p-3">
          <summary className="cursor-pointer font-medium text-sm">
            {t.app.pageStartupError.detailsLabel}
          </summary>
          <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words text-muted-foreground text-xs">
            {message}
          </pre>
        </details>
        <button
          className="mt-5 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm"
          type="button"
          onClick={() => window.location.reload()}
        >
          {t.app.pageStartupError.reloadButton}
        </button>
      </section>
    </main>
  );
}


function registerLearnitiveServiceWorker(logger: Logger): void {
  if (!('serviceWorker' in navigator)) return;
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') return;
  void navigator.serviceWorker
    .register(`/learnitive-texlive-sw.js?build=${encodeURIComponent(APP_BUILD_TIME)}`, { scope: '/', updateViaCache: 'none' })
    .catch((error) => logger.warn('Unable to register Learnitive offline service worker', error));
}

// Start only after all module-level bootstrap constants have initialized.
void main(startupLogger).catch((error) => {
  handleStartupFailure(error, startupLogger);
});
